import { useState, useEffect, useCallback, useRef } from 'react';

export interface ConnectivityState {
  isOnline: boolean;
  lastOnlineAt: Date | null;
}

export function useConnectivity(): ConnectivityState {
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [lastOnlineAt, setLastOnlineAt] = useState<Date | null>(() =>
    typeof navigator !== 'undefined' && navigator.onLine ? new Date() : null,
  );
  const wasOnlineRef = useRef(isOnline);

  const handleOnline = useCallback(() => {
    setIsOnline(true);
    setLastOnlineAt(new Date());
    wasOnlineRef.current = true;
  }, []);

  const handleOffline = useCallback(() => {
    setIsOnline(false);
    wasOnlineRef.current = false;
  }, []);

  useEffect(() => {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [handleOnline, handleOffline]);

  return { isOnline, lastOnlineAt };
}
